import React, { useRef, useEffect, useState } from "react";
import ProductPopup from "./ProductPopup";
import "./ProductTable.css";
import PopupChecklist from "./PopupChecklist";
import RowStatusIndicator from "./RowStatusIndicator";
import isEqual from "lodash.isequal";
import EditHistoryModal from "./EditHistoryModal";
import EditTablePopup from "./EditTablePopup";

const ProductTable = ({
  currentProducts,
  fields,
  dropdownData,
  selectedProducts,
  handleSelect,
  handleSelectAll,
  showEdit,
  showDelete,
  handleEdit,
  handleEditupdate,
  handleDelete,
  renderField,
  formatFieldName,
  selectedRow,
  setSelectedRow,
  pendingChanges,
  setPendingChanges,
  user,
  handleBulkUpdate,
  filteredProducts,
  status,
  api,
  handleRefresh,
}) => {
  const scrollRef = useRef();
  const [contextMenu, setContextMenu] = useState(null);
  const limitedFields = fields.slice(0, 20);
  const [editingCell, setEditingCell] = useState(null);
  const [editedValue, setEditedValue] = useState("");
  const [logModalData, setLogModalData] = useState({
    show: false,
    productId: null,
  });

  const allFieldNames = Array.isArray(fields)
    ? fields.map((f) => f.fields?.toString())
    : [];

  const userEditable = Array.isArray(user?.fieldsAccess)
    ? user.fieldsAccess.map((f) => f.toString().trim())
    : [];

  // ✅ Only common fields will be editable
  const editableFields = allFieldNames.filter((field) =>
    userEditable.includes(field)
  );

  const totals = {};

  fields.forEach((field) => {
    if (field.fields === "serialNo" || field.fields === "srNo") {
      totals[field.fields] = filteredProducts.length; // ✅ Total number of entries
    } else if (field.type === "number") {
      totals[field.fields] = filteredProducts.reduce((sum, item) => {
        const val = parseFloat(item[field.fields]);
        return sum + (isNaN(val) ? 0 : val);
      }, 0);
    } else if (field.type === "numberparts") {
      let totalReceived = 0;
      let totalExpected = 0;

      filteredProducts.forEach((item) => {
        const numberPartsValue = item?.[field.fields];
        if (
          typeof numberPartsValue === "string" &&
          numberPartsValue.includes("/")
        ) {
          const [received, expected] = numberPartsValue.split("/").map(Number);
          if (!isNaN(received)) totalReceived += received;
          if (!isNaN(expected)) totalExpected += expected;
        }
      });

      totals[field.fields] = `${totalReceived}/${totalExpected}`;
    }
  });

  useEffect(() => {
    const slider = scrollRef.current;
    let isDown = false;
    let startX, scrollLeft;

    const handleMouseDown = (e) => {
      isDown = true;
      slider.classList.add("active");
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    };

    const handleMouseUp = () => {
      isDown = false;
      slider.classList.remove("active");
    };

    const handleMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 2;
      slider.scrollLeft = scrollLeft - walk;
    };

    slider.addEventListener("mousedown", handleMouseDown);
    slider.addEventListener("mouseup", handleMouseUp);
    slider.addEventListener("mousemove", handleMouseMove);

    return () => {
      slider.removeEventListener("mousedown", handleMouseDown);
      slider.removeEventListener("mouseup", handleMouseUp);
      slider.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const handleContextMenu = (e, product) => {
    e.preventDefault();
    setContextMenu({
      x: e.pageX,
      y: e.pageY,
      product,
    });
  };

  const closeContextMenu = () => setContextMenu(null);
  const handleCellClick = (rowId, fieldName, currentValue) => {
    setEditingCell({ rowId, fieldName });
    setEditedValue(currentValue || "");
  };

  const canEditRow = (product) => {
    const currentUserId = user?._id;
    const currentUserRole = user?.role;

    // ✅ Super roles bypass ownership check
    const isAdminMaster = ["Admin Master", "Super Admin"].includes(
      currentUserRole
    );
    if (isAdminMaster) return true;

    const editableOwners = [
      product?.CreatedBy,
      ...(Array.isArray(product?.fabVendor) ? product.fabVendor : []),
      ...(Array.isArray(product?.UploadingEng) ? product.UploadingEng : []),
      ...(Array.isArray(product?.ErectionEng) ? product.ErectionEng : []),
    ];

    // Normalize IDs for comparison
    const userIds = editableOwners.map((owner) =>
      typeof owner === "object" ? owner._id : owner
    );

    return userIds.includes(currentUserId);
  };

  const locationFields = fields
    .filter((f) => f.type === "location")
    .map((f) => f.fields);

  const NumberPartsEditor = ({
    product,
    field,
    editedValue,
    setEditedValue,
    setPendingChanges,
    setEditingCell,
    currentProducts,
  }) => {
    const initialParts = (editedValue || "").split("/");
    const [received, setReceived] = useState(initialParts[0] || "");
    const [total, setTotal] = useState(initialParts[1] || "");

    const handleSave = () => {
      const finalValue = `${received}/${total}`;
      const updated = [...currentProducts];
      const index = updated.findIndex((p) => p._id === product._id);
      if (index !== -1) {
        updated[index][field.fields] = finalValue;
        setPendingChanges((prev) => ({
          ...prev,
          [product._id]: {
            ...(prev[product._id] || {}),
            [field.fields]: finalValue,
          },
        }));
      }
      setEditedValue(finalValue);
      setEditingCell(null);
    };

    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <input
          type="number"
          value={received}
          onChange={(e) => setReceived(e.target.value)}
          placeholder="0"
          style={{
            width: "40px",
            textAlign: "center",
            padding: "2px 4px",
            fontSize: "14px",
          }}
          autoFocus
        />
        <span style={{ margin: "0 4px", fontSize: "16px", fontWeight: "bold" }}>
          /
        </span>
        <input
          type="number"
          value={total}
          onChange={(e) => setTotal(e.target.value)}
          placeholder="0"
          style={{
            width: "40px",
            textAlign: "center",
            padding: "2px 4px",
            fontSize: "14px",
          }}
        />
        <button
          onClick={handleSave}
          style={{
            marginLeft: "8px",
            backgroundColor: "#4CAF50",
            color: "white",
            border: "none",
            padding: "2px 8px",
            cursor: "pointer",
            fontSize: "12px",
            borderRadius: "4px",
            transition: "background-color 0.2s ease",
          }}
        >
          ✔
        </button>
      </div>
    );
  };

  const FileUploadButton = ({ fieldName, productId, onUploadSuccess }) => {
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);

    const handleFileChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Check file size (e.g., 5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size should be less than 5MB");
        return;
      }

      setIsUploading(true);

      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(
          "https://rohan-billing-lifecycle-backend.vercel.app/billing/upload",
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        const result = await response.json();
        if (result.viewLink) {
          onUploadSuccess(productId, fieldName, result.viewLink);
        }
      } catch (error) {
        console.error("Upload failed:", error);
        alert("File upload failed. Please try again.");
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    };

    return (
      <div className="file-upload-container">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
          id={`file-upload-${productId}-${fieldName}`}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" // Specify accepted file types
        />
        <label
          htmlFor={`file-upload-${productId}-${fieldName}`}
          className="file-upload-button"
          title="Upload file"
        >
          {isUploading ? (
            <span>
              <i className="fas fa-spinner fa-spin"></i> Uploading
            </span>
          ) : (
            <span>
              <i className="fas fa-upload"></i>{" "}
            </span>
          )}
        </label>
      </div>
    );
  };

  return (
    <div onClick={closeContextMenu} style={{ overflowX: "auto" }}>
      <div className="table-scroll-wrapper" ref={scrollRef}>
        {Object.keys(pendingChanges).length > 0 && (
          <div style={{ padding: "10px", textAlign: "right" }}>
            <button
              onClick={handleBulkUpdate} // 🔁 Uses centralized function from ProductList
              className="save-changes-btn"
            >
              <i className="fas fa-save"></i>
            </button>
          </div>
        )}

        {selectedProducts.length > 0 &&
          locationFields.some((fieldName) =>
            userEditable.includes(fieldName)
          ) && (
            <div style={{ textAlign: "right", padding: "10px" }}>
              <button
                className="location-update-btn"
                onClick={() => {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      const { latitude, longitude } = position.coords;
                      const newLocation = `${latitude},${longitude}`;

                      const updated = [...currentProducts];

                      selectedProducts.forEach((rowId) => {
                        const rowIndex = updated.findIndex(
                          (p) => p._id === rowId
                        );
                        if (rowIndex !== -1) {
                          locationFields.forEach((fieldName) => {
                            const isEditable =
                              user?.fieldsAccess?.includes(fieldName);
                            if (!isEditable) return;

                            updated[rowIndex][fieldName] = newLocation;

                            setPendingChanges((prev) => ({
                              ...prev,
                              [rowId]: {
                                ...(prev[rowId] || {}),
                                [fieldName]: newLocation,
                              },
                            }));
                          });
                        }
                      });
                    },
                    (err) => {
                      alert("Failed to get location: " + err.message);
                    }
                  );
                }}
              >
                <i className="fas fa-map-marker-alt"></i>
              </button>
            </div>
          )}

        <table className="product-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={
                    selectedProducts.length === currentProducts.length &&
                    currentProducts.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th>Status</th>
              {limitedFields.map((field, index) => (
                <th
                  key={`${field.fields}-${index}`}
                  title={field.inotations || formatFieldName(field.fields)}
                >
                  {field.title || formatFieldName(field.fields)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentProducts.map((product) => (
              <tr
                key={product._id}
                onContextMenu={(e) => handleContextMenu(e, product)}
                style={{ cursor: "pointer" }}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedProducts.includes(product._id)}
                    onChange={() => handleSelect(product._id)}
                  />
                </td>
                <td>
                  <RowStatusIndicator product={product} status={status} />
                </td>
                {limitedFields.map((field, index) => {
                  const value = product[field.fields];
                  const isFileField = field.type === "file";
                  return (
                    <td
                      key={`${product._id}-${field.fields}-${index}`}
                      className={!value ? "empty-cell" : ""}
                      onDoubleClick={() => {
                        const fieldKey = field.fields?.toString();
                        const allowEdit =
                          editableFields.includes(fieldKey) &&
                          canEditRow(product);

                        if (allowEdit) {
                          handleCellClick(product._id, fieldKey, value);
                        }
                      }}
                    >
                      {editingCell?.rowId === product._id &&
                      editingCell?.fieldName === field.fields ? (
                        field.api && field.multiple ? (
                          <>
                            <div
                              className="multi-select-display"
                              onClick={() =>
                                setEditingCell({
                                  rowId: product._id,
                                  fieldName: field.fields,
                                  showPopup: true,
                                })
                              }
                            >
                              {Array.isArray(value) && value.length > 0 ? (
                                value.map((val) => {
                                  const label =
                                    val && typeof val === "object"
                                      ? val.name || val.userId || "Unnamed"
                                      : dropdownData[field.fields]?.find(
                                          (opt) => opt.id === val
                                        )?.label ||
                                        val ||
                                        "—";

                                  return (
                                    <span
                                      key={val?._id || label || index}
                                      className="multi-chip"
                                    >
                                      {label}
                                    </span>
                                  );
                                })
                              ) : (
                                <span className="placeholder">No users</span>
                              )}
                            </div>

                            {editingCell?.rowId === product._id &&
                              editingCell?.fieldName === field.fields &&
                              editingCell?.showPopup &&
                              (field.editPopUp === "true" &&
                              field.popupCalling ? (
                                Array.isArray(product[field.fields]) &&
                                product[field.fields].length > 1 &&
                                !editingCell.popupEntryId ? (
                                  // 👉 Case: Multiple IDs to choose from
                                  <div className="popup-select-multiple">
                                    <p>Select an entry to edit:</p>
                                    {product[field.fields].map((entryId) => (
                                      <button
                                        key={entryId}
                                        onClick={() =>
                                          setEditingCell((prev) => ({
                                            ...prev,
                                            popupEntryId: entryId,
                                          }))
                                        }
                                        style={{
                                          margin: "4px",
                                          padding: "4px 10px",
                                        }}
                                      >
                                        {entryId}
                                      </button>
                                    ))}
                                  </div>
                                ) : (
                                  <EditTablePopup
                                    id={
                                      editingCell?.popupEntryId ||
                                      (Array.isArray(product[field.fields])
                                        ? product[field.fields][0]
                                        : product[field.fields])
                                    }
                                    api={field.api}
                                    fields={field.popupCalling}
                                    onSuccess={handleRefresh}
                                  />
                                )
                              ) : (
                                <PopupChecklist
                                  options={dropdownData[field.fields] || []}
                                  selectedValues={
                                    Array.isArray(value)
                                      ? value
                                          .filter(Boolean) // ✅ filters out null/undefined
                                          .map((val) =>
                                            typeof val === "object"
                                              ? val._id
                                              : val
                                          )
                                      : []
                                  }
                                  onChange={(updatedValues) => {
                                    const currentValues = Array.isArray(value)
                                      ? value
                                          .filter(Boolean)
                                          .map((val) =>
                                            typeof val === "object"
                                              ? val._id
                                              : val
                                          )
                                      : [];

                                    if (
                                      !isEqual(currentValues, updatedValues)
                                    ) {
                                      const updated = [...currentProducts];
                                      const index = updated.findIndex(
                                        (p) => p._id === product._id
                                      );
                                      if (index !== -1) {
                                        updated[index][field.fields] =
                                          updatedValues;

                                        setPendingChanges((prev) => ({
                                          ...prev,
                                          [product._id]: {
                                            ...(prev[product._id] || {}),
                                            [field.fields]: updatedValues,
                                          },
                                        }));

                                        // Update visual value immediately
                                        product[field.fields] = updatedValues;
                                      }
                                    }

                                    setEditingCell(null); // Close popup
                                  }}
                                  onClose={() => setEditingCell(null)}
                                />
                              ))}
                          </>
                        ) : isFileField ? (
                          <div className="file-field-container">
                            <div className="file-actions">
                              <button
                                onClick={() =>
                                  value && window.open(value, "_blank")
                                }
                                className="view-file-button"
                                title="View file"
                                disabled={!value}
                              >
                                <i className="fas fa-eye"></i>
                              </button>

                              <button
                                onClick={() =>
                                  value && navigator.clipboard.writeText(value)
                                }
                                className="copy-link-button"
                                title="Copy link"
                                disabled={!value}
                              >
                                <i className="fas fa-copy"></i>
                              </button>

                              <FileUploadButton
                                fieldName={field.fields}
                                productId={product._id}
                                onUploadSuccess={(id, fieldName, link) => {
                                  const updated = [...currentProducts];
                                  const index = updated.findIndex(
                                    (p) => p._id === id
                                  );
                                  if (index !== -1) {
                                    updated[index][fieldName] = link;
                                    setPendingChanges((prev) => ({
                                      ...prev,
                                      [id]: {
                                        ...(prev[id] || {}),
                                        [fieldName]: link,
                                      },
                                    }));
                                  }
                                }}
                              />
                            </div>
                          </div>
                        ) : field.type === "numberparts" ? (
                          <NumberPartsEditor
                            product={product}
                            field={field}
                            editedValue={editedValue}
                            setEditedValue={setEditedValue}
                            setPendingChanges={setPendingChanges}
                            setEditingCell={setEditingCell}
                            currentProducts={currentProducts}
                          />
                        ) : (
                          <input
                            type={field.type === "date" ? "date" : "text"}
                            value={editedValue}
                            onChange={(e) => setEditedValue(e.target.value)}
                            onBlur={() => {
                              const updated = [...currentProducts];
                              const index = updated.findIndex(
                                (p) => p._id === product._id
                              );
                              if (
                                index !== -1 &&
                                !isEqual(product[field.fields], editedValue)
                              ) {
                                updated[index][field.fields] = editedValue;
                                setPendingChanges((prev) => ({
                                  ...prev,
                                  [product._id]: {
                                    ...(prev[product._id] || {}),
                                    [field.fields]: editedValue,
                                  },
                                }));
                              }
                              setEditingCell(null);
                            }}
                            autoFocus
                          />
                        )
                      ) : (
                        (() => {
                          const raw = product[field.fields];
                          let displayText = "";

                          // 🎯 Case 1: Show image if field is photo
                          if (field.fields === "photo") {
                            return (
                              <img
                                src={value}
                                alt={product.title}
                                style={{ maxWidth: "50px" }}
                              />
                            );
                          }

                          // 🎯 Case 2: API field
                          if (field.api) {
                            if (Array.isArray(raw)) {
                              displayText = raw
                                .map((item) =>
                                  typeof item === "object" && item !== null
                                    ? item?.name || item?.userId || "Unnamed"
                                    : dropdownData[field.fields]?.find(
                                        (opt) => opt.id === item
                                      )?.label || item
                                )
                                .join(", ");
                            } else if (
                              typeof raw === "object" &&
                              raw !== null
                            ) {
                              displayText =
                                raw?.name || raw?.userId || "Unnamed User";
                            } else {
                              displayText = raw || "";
                            }
                          }

                          // 🎯 Case 3: Date field
                          else if (field.type === "date" && value) {
                            displayText = new Date(value).toLocaleDateString(
                              "en-GB"
                            );
                          }

                          // 🎯 Case 4: Fallback for plain values
                          else {
                            displayText =
                              typeof value === "string"
                                ? value
                                : String(value ?? "");
                          }

                          // 🎯 Case 5: Long string with Read More/Read Less
                          const maxLength = 80;
                          const isExpanded =
                            editingCell?.rowId === product._id &&
                            editingCell?.fieldName === field.fields;

                          return displayText.length > maxLength ? (
                            <div className="expandable-text">
                              {isExpanded
                                ? displayText
                                : `${displayText.slice(0, maxLength)}...`}
                              <span
                                className="read-more-btn"
                                onClick={() =>
                                  setEditingCell(
                                    isExpanded
                                      ? null
                                      : {
                                          rowId: product._id,
                                          fieldName: field.fields,
                                        }
                                  )
                                }
                              >
                                {isExpanded ? " Read Less" : " Read More"}
                              </span>
                            </div>
                          ) : (
                            displayText ||
                              renderField(
                                displayText,
                                field.fields,
                                product._id
                              )
                          );
                        })()
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {currentProducts.length === 0 && (
              <tr>
                <td colSpan={limitedFields.length + 1} className="no-data-cell">
                  No data available for the current filter.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} className="total-label-cell">
                Total
              </td>
              {limitedFields.map((field, index) => (
                <td key={field.fields + "-footer"} className="total-value-cell">
                  {field.fields === "serialNo"
                    ? totals[field.fields] // ✅ Direct count
                    : field.type === "numberparts"
                    ? totals[field.fields] || "0/0" // ✅ received/total
                    : field.type === "number"
                    ? (totals[field.fields] || 0).toFixed(2) // ✅ summed number
                    : ""}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      {contextMenu && (
        <ul
          className="context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()} // prevent div click close
        >
          <li
            onClick={() => {
              setSelectedRow(contextMenu.product);
              setContextMenu(null);
            }}
          >
            <i className="fas fa-eye"></i> View
          </li>
          {showEdit && (
            <li
              onClick={() => {
                handleEdit(contextMenu.product._id);
                setContextMenu(null);
              }}
            >
              <i className="fas fa-edit"></i> Edit
            </li>
          )}
          {showDelete && (
            <li
              onClick={() => {
                handleDelete(contextMenu.product._id);
                setContextMenu(null);
              }}
            >
              <i className="fas fa-trash-alt"></i> Delete
            </li>
          )}
          <li
            onClick={() => {
              setLogModalData({
                show: true,
                productId: contextMenu.product._id,
              });
              setContextMenu(null);
            }}
          >
            <i className="fas fa-history"></i> View Logs
          </li>
        </ul>
      )}

      {selectedRow && (
        <ProductPopup
          product={selectedRow}
          fields={fields}
          dropdownData={dropdownData}
          onClose={() => setSelectedRow(null)}
          handleEdit={handleEdit}
          handleDelete={showDelete ? handleDelete : null}
          showEdit={showEdit}
          showDelete={showDelete}
        />
      )}

      {logModalData.show && (
        <EditHistoryModal
          entryId={logModalData.productId}
          apiUrl={`${api}`} // ✅ correct
          onClose={() => setLogModalData({ show: false, productId: null })}
        />
      )}
    </div>
  );
};

export default ProductTable;
